import * as React from 'react';
import {
  Box, Button, Grid, IconButton, Paper, Typography,
} from '@material-ui/core';
import type { AxiosError } from 'axios';
import axios from 'axios';
import useSWR from 'swr';
import { useSnackbar } from 'notistack';
import type { GridColDef } from '@material-ui/data-grid';
import { DataGrid } from '@material-ui/data-grid';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { useNavigate } from 'react-router-dom';
import globalUseStyles from '../../../globalClasses';
import type { IJobInfo } from './JobInterfaces';
import JobActionMenu from './JobActionMenu';

const AllJobs: React.FC = () => {
  const globalStyles = globalUseStyles();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [jobId, setjobId] = React.useState<string | null>(null);

  const jobsFetcher = (url: string) => axios.get<IJobInfo[]>(url)
    .then((response) => response.data)
    .catch((e: AxiosError) => {
      enqueueSnackbar(`There was the following error when fetching the jobs: ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
    });

  const { data, mutate } = useSWR(
    ['/job/getJobs'],
    (url) => jobsFetcher(url),
  );

  const handleActionMenuClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    if (!anchorEl) {
      setjobId(id);
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setjobId(null);
    setAnchorEl(null);
  };

  const handleDelete = () => {
    axios.post('/job/deleteJob', { JobId: jobId })
      .then(() => {
        mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
        enqueueSnackbar('Job deleted successfully.', { variant: 'success' });
      })
      .catch((error: AxiosError) => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
      });
  };

  const displayVertIcon = (jobIdParameter: string) => (
    <IconButton
      aria-label="more"
      aria-controls="long-menu"
      aria-haspopup="true"
      id="table-kabob"
      onClick={(e) => handleActionMenuClick(e, jobIdParameter)}
    >
      <MoreVertIcon />
    </IconButton>
  );

  const manualAddColumns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'type', headerName: 'Type', flex: 0.5 },
    {
      field: 'fields',
      headerName: 'Fields',
      flex: 1.5,
      renderCell: ({ value }) => (Array.isArray(value) ? value.join(', ') : value),
    },
    { field: 'station', headerName: 'Station', flex: 0.5 },
    {
      field: 'id',
      type: 'string',
      headerName: 'Actions',
      flex: 0.5,
      renderCell: ({ value }) => (
        ((typeof (value) === 'string')
          ? displayVertIcon(value)
          : <></>
        )
      ),
    },
  ];

  return (
    <Grid>
      <Grid container>
        <Grid container>
          <Grid item xs={10}>
            <Box pb={3} pt={3}>
              <Typography variant="h4" className={globalStyles.labelText}>{'Jobs'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={2}>
            <Box pb={3} pt={3}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={() => navigate('/job')}
              >
                {'Add new Job'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Box pb={3}>
          <Paper elevation={4}>
            <Box p={3}>
              <Typography variant="h6">{'Jobs'}</Typography>
            </Box>
            {data
              && (
                <>
                  <DataGrid
                    rows={data || []}
                    columns={manualAddColumns}
                    autoHeight
                    className={globalStyles.transparentDataGrid}
                    sortModel={[
                      {
                        field: 'name',
                        sort: 'asc',
                      },
                    ]}
                  />
                  {
                    jobId && anchorEl && (
                      <JobActionMenu
                        jobId={jobId}
                        anchorEl={anchorEl}
                        handleClose={handleClose}
                        handleDelete={handleDelete}
                      />
                    )
                  }
                </>
              )}
          </Paper>
        </Box>
      </Grid>
    </Grid>
  );
};

export default AllJobs;
